import React, {useEffect, useState} from 'react'
import {Trans, useTranslation} from 'react-i18next'
import {RouteComponentProps, useHistory, useLocation} from 'react-router-dom'

import EastIcon from '@mui/icons-material/East'
import {Box, Divider, Stack} from '@mui/material'
import {Theme} from '@mui/material/styles'
import {PGButton, PGTypography} from '@progynyglobal/pg-ui-library'
import {useSnackbar} from 'notistack'
import {makeStyles} from 'tss-react/mui'

import BackButton from '@progyny/components/Buttons/BackButton'
import ContactSupport from '@progyny/components/ContactSupport'
import PageLoader from '@progyny/components/PageLoader'
import {
  useAddPartnerMutation,
  useMeQuery,
  useResendPartnerConsentMutation,
} from '@progyny/generated/graphql'
import {useErrorCodes} from '@progyny/hooks/useErrorCodes'
import PAForm from '@progyny/pages/Budget/PartnerAgreement/PAForm'
import {FormValues} from '@progyny/pages/Budget/PartnerAgreement/PAFormModel'
import {
  AvatarWithOnlyCheckmark,
  AvatarWithOnlyWaitingIcon,
  AvatarWithRestart,
} from '@progyny/pages/Budget/PartnerAgreement/helpers/Avatars'
import {SubpageOneColGrid} from '@progyny/pages/Profile/fragments/grid'
import {RouteEnum, useRoutes} from '@progyny/routes'
import {useMeStore} from '@progyny/store/meStore'

type LocationState = {
  fromProfile?: boolean | false
  fromBudget?: boolean | false
  returnTo?: string | undefined
}

type PartnerAgreementProps = RouteComponentProps & {
  onPAFormBackBtn?: () => void
}

const useStyles = makeStyles()((theme: Theme) => ({
  proceedHeadingFromBudget: {
    width: '50%',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  widthWide: {
    width: '60%',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  widthNarrow: {
    width: '45%',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
}))

const PartnerAgreement: React.FC<PartnerAgreementProps> = (props) => {
  const {classes} = useStyles()
  const {t} = useTranslation()
  const [isSuccess, setSuccess] = useState(false)
  const [isEmailResent, setEmailResent] = useState(false)
  const {refetch, loading: userLoading} = useMeQuery()
  const user = useMeStore((s) => s.user)
  const getErrorMessage = useErrorCodes()
  const [hasRefetched, setHasRefetched] = useState(false)
  const location = useLocation<LocationState>()
  const passedState = location?.state
  const history = useHistory()
  const {getPath} = useRoutes()
  const [resendPartnerAgreement, {loading: isResendPALoading}] =
    useResendPartnerConsentMutation()
  const parentRoute = props.match.url
    .split('/')
    .filter(Boolean)
    .slice(0, -1)
    .join('/')

  const isSimpleRoute = props.match.url === '/partner-agreement'

  const routeTo = isSimpleRoute
    ? `/budget/reimbursement`
    : `/${parentRoute}/treatment`
  const [addPartner, {loading}] = useAddPartnerMutation()
  const {enqueueSnackbar} = useSnackbar()

  useEffect(() => {
    refetch().finally(() => {
      setHasRefetched(true)
    })
  }, [refetch])

  function handleSubmit(values: FormValues, selectedPartnerId: File) {
    addPartner({
      errorPolicy: 'all',
      variables: {
        memberCity: values?.city,
        memberCountry: values?.country,
        memberPostcode: values?.postalCode,
        memberStreetAddress: values?.homeAddress,
        partnerDateOfBirth: values?.partnerDob,
        partnerEmail: values?.partnerEmail,
        partnerFirstName: values?.partnerFname,
        partnerLastName: values?.partnerLname,
        sameEmployer: values?.partnerCompany === 'yes',
        sameHousehold: values?.isSameAddressForPartner === 'yes',
        idDocument: selectedPartnerId,
        partnerEmployeeNumber: values?.partnerEnumber,
        partnerCity: values?.partnerCity,
        partnerCountry: values?.partnerCountry,
        partnerPostcode: values?.partnerPostalCode,
        docType: values?.documentType,
        partnerStreetAddress: values?.partnerHomeAddress,
      },
    })
      .then((res) => {
        if (res.data?.addPartner?.success) {
          setSuccess(true)
          refetch()
        } else if (res.data?.addPartner?.errors?.length) {
          const errors = res.data?.addPartner?.errors
          const message = errors[0]?.messages[0]
          return enqueueSnackbar({
            message: message ?? t('error_codes_unknown_error'),
            variant: 'error',
          })
        } else if (res?.errors?.length) {
          const message = res?.errors[0]?.message
          return enqueueSnackbar({
            message: message ?? t('error_codes_unknown_error'),
            variant: 'error',
          })
        }
      })
      .catch((error) => console.error(error))
  }

  const navigateToTreatment = () => {
    return history.push({
      pathname: routeTo,
      state: {from: location.pathname},
    })
  }

  function handleResendEmail() {
    resendPartnerAgreement()
      .then((res) => {
        if (res.data?.resendPartnerConsentEmail?.success) {
          setEmailResent(true)
          setSuccess(true)
          refetch()
        } else if (res.data?.resendPartnerConsentEmail?.errors) {
          const errors = res.data?.resendPartnerConsentEmail?.errors
          const message = errors[0]?.messages[0]
          return enqueueSnackbar({
            message: getErrorMessage(message as string),
            variant: 'error',
          })
        }
      })
      .catch((error) => console.error(error))
  }

  if (loading || isResendPALoading || userLoading) {
    return (
      <Box minHeight={140}>
        <PageLoader position={'center'} />
      </Box>
    )
  }

  const isUserLoaded =
    hasRefetched && user?.member?.partnerConsentStatus !== undefined

  if (!isUserLoaded) return null

  const isReview = !!(
    user.member.partnerDetails &&
    (user.member.partnerConsentStatus === 'not_done' ||
      user.member.partnerConsentStatus === 'accepted')
  )

  if (passedState?.fromProfile) {
    return (
      <Box mt={15}>
        <SubpageOneColGrid title={t('profile_partner_information')} lgSize={8}>
          <PAForm
            isReview={isReview}
            onBackBtn={props.onPAFormBackBtn}
            onPASubmit={handleSubmit}
            fromProfile={passedState?.fromProfile}
          />
        </SubpageOneColGrid>
      </Box>
    )
  }

  return (
    <Stack padding={isSimpleRoute ? 4 : 0} maxWidth='100%'>
      {(isReview || isSuccess || !props.onPAFormBackBtn) && (
        <Box mt={isSimpleRoute ? 9 : 0}>
          <BackButton
            text={t('common_back')}
            onBackClick={() =>
              passedState?.fromBudget
                ? history.push(`/budget`)
                : passedState?.returnTo
                  ? history.push(passedState?.returnTo)
                  : history.push(
                      `${getPath(RouteEnum.BUDGET)}/reimbursement/claim`
                    )
            }
          />
        </Box>
      )}
      {!isSuccess ? (
        !!user?.member?.partnerDetails ? (
          user?.member?.partnerConsentStatus === 'not_done' ? (
            <Stack
              justifyContent={'center'}
              mt={12}
              mb={4}
              px={{xs: 2, md: '6rem'}}
              alignItems={'center'}
            >
              <AvatarWithOnlyWaitingIcon />
              <PGTypography
                textAlign={'center'}
                variant='header'
                className={classes.widthWide}
                style={{marginBottom: 16}}
              >
                {t('budget_partner_agreement_waiting_for_partner_s_approval')}
              </PGTypography>
              <PGTypography
                variant='subtitle'
                textAlign={'center'}
                className={classes.widthNarrow}
              >
                <Trans i18nKey='budget_partner_agreement_we_have_sent_partner_agreement' />
              </PGTypography>
              <Stack
                mt={7}
                flexDirection={'row'}
                alignItems={'center'}
                justifyContent={'center'}
                gap={2}
              >
                <PGButton
                  onClick={handleResendEmail}
                  label={t('budget_partner_agreement_resend_partner_agreement')}
                />
              </Stack>
              <PGTypography
                variant='subtitle'
                textAlign={'center'}
                className={classes.widthNarrow}
                style={{marginTop: 80, paddingTop: 40}}
              >
                <Trans i18nKey='budget_partner_agreement_has_something_changed_since_last' />
              </PGTypography>
              <ContactSupport
                textKey='budget_partner_agreement_helpers_support_if_you_have_any_questions'
                my={1}
                alignItems={'center'}
              />
              <Divider sx={{width: '100%', pb: 3, mb: 3}} />
            </Stack>
          ) : user?.member?.partnerConsentStatus === 'accepted' ? (
            <Box
              display='flex'
              flexDirection={passedState?.fromBudget ? 'row' : 'column'}
              alignItems={passedState?.fromBudget ? 'baseline' : 'center'}
              justifyContent={
                passedState?.fromBudget ? 'space-between' : 'center'
              }
              mt={3}
              mb={4}
            >
              <Box
                display='flex'
                justifyContent='center'
                alignItems={'center'}
                mb={6}
              >
                <PGTypography
                  textAlign='center'
                  variant='header'
                  className={
                    passedState?.fromBudget
                      ? classes.proceedHeadingFromBudget
                      : classes.widthWide
                  }
                >
                  {passedState?.fromBudget
                    ? t(
                        'budget_partner_agreement_partner_agreement_is_approved_please'
                      )
                    : t(
                        'budget_partner_agreement_we_will_proceed_with_reimbursement'
                      )}
                </PGTypography>
              </Box>

              <PGButton
                variant={passedState?.fromBudget ? 'outlined' : 'contained'}
                endIcon={<EastIcon />}
                onClick={navigateToTreatment}
                label={t('navigation_start_claim_request')}
              />

              {!passedState?.fromBudget && (
                <>
                  <PGTypography
                    variant='subtitle'
                    textAlign={'center'}
                    className={classes.widthNarrow}
                    style={{marginTop: 48}}
                  >
                    <Trans i18nKey='budget_partner_agreement_has_something_changed_since_last' />
                  </PGTypography>
                  <ContactSupport
                    textKey='budget_partner_agreement_helpers_support_if_you_have_any_questions'
                    mt={{xs: 4, md: 1}}
                    mb={1}
                    alignItems={'center'}
                  />
                </>
              )}
            </Box>
          ) : (
            <Stack
              justifyContent={'center'}
              mt={12}
              mb={4}
              px={{xs: 2, md: '6rem'}}
              alignItems={'center'}
            >
              <AvatarWithOnlyWaitingIcon />
              <PGTypography
                textAlign={'center'}
                variant='header'
                className={classes.widthWide}
                style={{marginBottom: 16}}
              >
                {t('budget_partner_agreement_partner_agreement_declined')}
              </PGTypography>
              <PGTypography
                variant='subtitle'
                textAlign={'center'}
                className={classes.widthNarrow}
              >
                <Trans i18nKey='budget_partner_agreement_we_regret_to_inform_you' />
              </PGTypography>
              <PGTypography
                variant='subtitle'
                textAlign={'center'}
                className={classes.widthNarrow}
                style={{marginTop: 16}}
              >
                <Trans i18nKey='budget_partner_agreement_you_can_discuss_matter_further' />
              </PGTypography>
              <Box mt={7}>
                <PGButton
                  onClick={handleResendEmail}
                  endIcon={<AvatarWithRestart />}
                  label={t('budget_partner_agreement_restart_process')}
                />
              </Box>
              <Divider sx={{width: '100%', pb: 3, mb: 3}} />
            </Stack>
          )
        ) : (
          <Stack alignItems={'center'} gap={2} mb={10}>
            <PGTypography variant='header'>
              {t('budget_partner_agreement_let_s_set_up_your')}
            </PGTypography>
            <PGTypography
              variant='subtitle'
              textAlign={'center'}
              style={{maxWidth: '550px'}}
            >
              <Trans i18nKey='budget_partner_agreement_we_need_to_know_more' />
            </PGTypography>
          </Stack>
        )
      ) : isEmailResent ? (
        <Stack justifyContent={'center'} mt={12} mb={4} alignItems={'center'}>
          <AvatarWithOnlyCheckmark />
          <PGTypography
            textAlign={'center'}
            variant='header'
            className={classes.widthNarrow}
            style={{marginBottom: 16}}
          >
            {t('budget_partner_agreement_partner_agreement_resent')}
          </PGTypography>
          <PGTypography
            variant='subtitle'
            textAlign={'center'}
            className={classes.widthNarrow}
          >
            <Trans i18nKey='budget_partner_agreement_we_ve_just_resent_agreement' />
          </PGTypography>
          <PGTypography
            variant='subtitle'
            textAlign={'center'}
            className={classes.widthNarrow}
            style={{marginTop: 16, marginBottom: 16}}
          >
            <Trans i18nKey='budget_partner_agreement_please_ask_them_to_check' />
          </PGTypography>
        </Stack>
      ) : (
        <Stack justifyContent={'center'} mt={12} mb={4} alignItems={'center'}>
          <AvatarWithOnlyCheckmark />
          <PGTypography
            textAlign={'center'}
            variant='header'
            className={classes.widthWide}
            style={{marginBottom: 16}}
          >
            {t('budget_partner_agreement_partner_agreement_has_been_sent')}
          </PGTypography>
          <PGTypography
            variant='subtitle'
            textAlign={'center'}
            className={classes.widthWide}
          >
            <Trans i18nKey='budget_partner_agreement_we_will_notify_you_via' />
          </PGTypography>
        </Stack>
      )}
      {!isSuccess && (
        <PAForm
          isReview={isReview}
          onBackBtn={props.onPAFormBackBtn}
          onPASubmit={handleSubmit}
        />
      )}
      {isSuccess && (
        <ContactSupport
          textKey='budget_partner_agreement_helpers_support_if_you_have_any_questions'
          mt={16}
          my={3}
          alignItems={'center'}
        />
      )}
    </Stack>
  )
}

export default PartnerAgreement
